import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useRef, useMemo } from "react";
import CompanyImage from "../companies/companyImage";
import CategoryImage from "./categoryImage";
import CategoryInfo from "./categoryInfo";
import i18next, { t } from "i18next";
import LoaderProgressBar from "../../commons/loaderProgressBar";
import { formatPrice } from "../../../utils/formatPrice";
import { formatCurrencyWithSymbol } from "../../../utils/formatCurrencyWithSymbol";
// AB#8269 — Defense-in-depth window: when EVERY incoming item still has a
// zero price within this many ms of mount and `isFinal` is already true, we
// treat the snapshot as a stale-Redis / producer-side race rather than a
// genuine "no inventory" result and keep showing loaders. The window is
// generous enough to cover the post-remount FilterResult round-trip on a
// healthy network but short enough to never mask a real empty result for
// long. Tier 1 (BookingEngine) is the primary fix; this is the secondary.
const RECENT_MOUNT_WINDOW_MS = 2000;
const RenderCategoryItems = ({ items, type, convertPriceMemoized, handleSelect, translations, currency, filters, isFinal = false, 
// WI #8534 — `isComplete` is the post-#8421 BE deterministic completion
// signal (FilterPagingClientService emits it exactly once when the
// orchestrator AND all paging is genuinely done). When the prop is
// OMITTED (undefined), we default to `true` so existing callers that
// pre-date this WI keep the AB#8241 3-state machine (Empty-definitive
// collapses on `isFinal=true && quantity===0`). When the prop is
// EXPLICITLY `false`, we are post-WI#8534 and in the new
// `Pending-bypass-active` branch — keep the spinner alive.
isComplete, collections = [], }) => {
    const effectiveIsComplete = isComplete !== false;
    let currencies = currency;
    // AB#8269 — capture the time of the FIRST render of this component instance
    // so the all-zero guard below can distinguish a freshly remounted search
    // (where late-arriving FilterResult is expected) from a steady-state empty
    // result (where collapsing is correct).
    const mountTimeRef = useRef(Date.now());
    if (type == "category") {
        const lang = i18next.language.toUpperCase();
        items.forEach((element) => {
            var _a;
            const match = translations.find((t) => t.category == element.value);
            if (match) {
                element.name = ((_a = match.translations.find((t) => lang.includes(t.language.toUpperCase()))) === null || _a === void 0 ? void 0 : _a.text) || match.name || element.name;
            }
        });
    }
    // AB#8269 — Defense-in-depth all-zero guard. When `items.length > 0` AND
    // every item has `price === 0` AND `isFinal === true` AND we are still
    // within `RECENT_MOUNT_WINDOW_MS` of mount, the snapshot is almost certainly
    // a stale-Redis race (currency-change remount + cached `searchId` + already
    // terminal `prov:{searchId}` set in BookingEngine). Returning loaders here
    // keeps the pills on screen until the next priced FilterResult arrives,
    // instead of collapsing them via the `isEmptyFinal` short-circuit below.
    // The genuine `items.length === 0` empty case is unaffected — that branch
    // still falls through to the per-item `null` short-circuit (with no items
    // to map over, the resulting array is empty, matching prior behaviour).
    const allItemsZeroPriced = items.length > 0 &&
        items.every((i) => { var _a; return ((_a = i === null || i === void 0 ? void 0 : i.price) !== null && _a !== void 0 ? _a : 0) === 0; });
    const recentlyMounted = Date.now() - mountTimeRef.current < RECENT_MOUNT_WINDOW_MS;
    const allZeroFinalRace = isFinal && allItemsZeroPriced && recentlyMounted;
    const collectionLogoMap = useMemo(() => {
        const map = new Map();
        collections.forEach((c) => {
            var _a;
            if ((c === null || c === void 0 ? void 0 : c.title) && ((_a = c === null || c === void 0 ? void 0 : c.image) === null || _a === void 0 ? void 0 : _a.src)) {
                map.set(c.title.toLowerCase(), c.image.src);
            }
        });
        return map;
    }, [collections]);
    return items.map((category, index) => {
        var _a, _b;
        const { value, loadingData, price, title, image, passengers, bags, name } = category;
        const isRenter = type === "renter";
        // AB#8241 — 3-state render machine driven ONLY by the deterministic backend signal
        // `isFinal` (FilterResult.IsFinal from the WS payload, true when orchestrator complete
        // AND every provider has responded) and the derived `hasPrice`. The per-item
        // `loadingData` field from the backend is intentionally NOT part of the decision —
        // it may be `false` on intermediate slices before all providers are in, which is
        // exactly the trap that caused the $0/d flash in Bug #8051.
        //
        // AB#8534 — extended to a 4-state machine by adding the `isComplete` BE signal
        // (post-#8421 FilterPagingClientService emits `isComplete=true` exactly once when
        // the orchestrator AND all paging is genuinely done). `isComplete` is parallel
        // to `isFinal`, never a replacement. The new state is `Pending-bypass-active`,
        // a sibling of `Pending-streaming`: both render the spinner, but the new state
        // exists when the BE prematurely emits `IsFinal=true` and we are waiting for
        // the trailing `isComplete=true` frame.
        //
        //   1. Ready                  → hasPrice                                  → price pill
        //   2. Pending-streaming       → !hasPrice && !isFinal                    → loader pill
        //   3. Pending-bypass-active   → !hasPrice && isFinal && !isComplete      → loader pill (NEW)
        //   4. Empty-definitive        → !hasPrice && isFinal && isComplete       → return null
        //
        // `categoryCard` guarantees state 4 via the 30s `forcedFinal` safety even if the
        // backend never emits IsFinal=true, so loaders can never stick forever.
        //
        // AB#8269 — when the `allZeroFinalRace` flag (computed above the map) is
        // true, force the Pending branch instead of Empty-definitive so the pills do
        // not collapse during the post-remount stale-Redis race after a currency
        // switch. The genuine no-inventory branch (items.length === 0) is
        // unaffected because `allItemsZeroPriced` requires items.length > 0.
        //
        // Backward compat: callers that omit `isComplete` default to `false`,
        // which keeps the new state 3 active (no collapse) until `isComplete=true`
        // arrives OR the 30s `forcedFinal` safety net flips `isFinal=true` (in
        // which case `isComplete=false` keeps state 3 — but the safety net is a
        // last-resort signal already paired with `forcedFinal`, so callers wiring
        // through the post-#8421 signal pass `isComplete=true` alongside it).
        const hasPrice = price > 0;
        const isEmptyFinal = !hasPrice && isFinal && effectiveIsComplete && !allZeroFinalRace;
        if (isEmptyFinal) {
            return null;
        }
        const showLoader = !hasPrice;
        return (_jsx("div", { className: `categories__item ${isRenter
                ? ((_a = filters.renterFilters) === null || _a === void 0 ? void 0 : _a.includes(value))
                    ? "categories__item--active"
                    : ""
                : ((_b = filters.categoryFilters) === null || _b === void 0 ? void 0 : _b.includes(value))
                    ? "categories__item--active"
                    : ""}`, onClick: () => handleSelect(category, type), "data-user-analizer": "Filter", id: "filter" + (isRenter ? title.toLowerCase() : value), "category-tittle": isRenter ? title.toLowerCase() : value.toLowerCase(), children: _jsx("div", { className: "categories__item-inner", children: isRenter ? (_jsxs(_Fragment, { children: [_jsx(CompanyImage, { image: collectionLogoMap.get(title === null || title === void 0 ? void 0 : title.toLowerCase()) || (image === null || image === void 0 ? void 0 : image.src), loading: "lazy" }), showLoader ? (_jsx("span", { className: "category-loader category-loader--desktop", children: _jsx(LoaderProgressBar, {}) })) : (_jsxs(_Fragment, { children: [_jsx("span", { className: "category-price-from", children: t("from") }), _jsxs("span", { className: "category-price", children: [formatCurrencyWithSymbol(t(currency + "_f"), formatPrice(price, currency)), "/", t("d")] })] }))] })) : (_jsxs(_Fragment, { children: [_jsx(CategoryImage, { image: image, hide: false, loading: "lazy" }), _jsx(CategoryInfo, { name: name, passengers: passengers, bags: bags, hide: true, translations: translations, price: formatPrice(price, currency), currency: currency, loadingData: !hasPrice, isSearchCompleted: isFinal })] })) }) }, index));
    });
};
export default RenderCategoryItems;
